import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import type { TextSegment } from './search-lessons';

let nextId = 0;

/**
 * Одна строка урока в левой колонке библиотеки: кадр ролика и заголовок на всю ширину строки, не выше
 * двух строк — длинный обрезан многоточием и целиком виден в подсказке.
 * Активная строка — заливка и кольцо --sel на кадре, играющая — пауза вместо «play»; просмотренный
 * урок — затемнённый кадр с галочкой в углу, всегда. Действий на строке нет: просмотр и избранное
 * живут в шапке окна. Выбор урока — кнопка во всю строку под содержимым.
 * Подсветка совпадений приходит готовыми сегментами — без innerHTML.
 */
@Component({
  selector: '[onboarding-lesson-card]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, HlmTooltip],
  host: {
    // Прозрачные поля хоста — зазор между плашками, и он тоже нажимается: вся колонка — сплошные карточки.
    class: 'group/row block cursor-pointer py-1 text-left text-sm leading-compact text-coach-ink',
    '[class.is-active]': 'active()',
    '[class.is-viewed]': 'viewed()',
  },
  styles: `
    /* Волос и затемнение лежат слоем над постером: рамка у самого кадра ушла бы под картинку. */
    .th::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: var(--scrim-ctrl);
      box-shadow: inset 0 0 0 var(--border-width-hairline) var(--line3);
    }
    :host(.is-active) .th::after {
      box-shadow: inset 0 0 0 var(--border-width-frame) var(--sel);
    }
    :host(.is-viewed) .th::after {
      background: var(--scrim-ctrl-2);
    }
    mark {
      border-radius: var(--r-compact);
      background: var(--sel);
      color: var(--on-acc);
    }
  `,
  template: `
    <div
      class="relative flex items-center gap-3 rounded-[var(--r-s)] py-1.25 pl-1.5 pr-2 transition-colors
             duration-[var(--t-fast)] ease-[var(--ease-smooth)] motion-reduce:transition-none group-hover/row:bg-panel3"
      [class.bg-panel3]="active()"
    >
      <button
        type="button"
        class="absolute inset-0 z-0 cursor-pointer rounded-[var(--r-s)] outline-none focus-visible:ring-2
             focus-visible:ring-inset focus-visible:ring-[var(--acc-line)]"
        [attr.aria-current]="current() ? 'true' : null"
        [attr.aria-describedby]="descId"
      >
        <span class="sr-only">{{ titleText() }}</span>
      </button>
      <span class="th pointer-events-none relative h-10.75 w-16 shrink-0 overflow-hidden rounded-[var(--r-xs)] bg-stage-black">
        @if (poster(); as src) {
          <img [src]="src" alt="" class="size-full object-cover" loading="lazy" decoding="async" />
        }
        <span class="absolute inset-0 z-1 grid place-items-center text-on-media">
          <app-icon [name]="playing() ? 'pause' : 'play'" [size]="14" />
        </span>
        <!-- Отметка на кадре — только статус: видна у каждого просмотренного ролика, не нажимается. -->
        @if (viewed()) {
          <span
            class="mk absolute bottom-0.75 right-0.75 z-2 grid size-4 place-items-center rounded-full bg-on-media
                 text-stage-black"
            aria-hidden="true"
          >
            <app-icon name="check" [size]="10" />
          </span>
        }
      </span>
      <span class="pointer-events-none relative flex min-w-0 flex-1 flex-col">
        <!-- Заголовок уже звучит именем кнопки выбора — второй раз читалке он не нужен. Больше двух строк
           не бывает: обрезанный многоточием заголовок целиком показывает подсказка по наведению. -->
        <span
          #titleEl
          class="pointer-events-auto line-clamp-2"
          aria-hidden="true"
          [class.font-medium]="active()"
          [class.text-c2]="viewed() && !active()"
          [hlmTooltip]="clamped() ? titleText() : ''"
          (mouseenter)="measureClamp(titleEl)"
        >
          @for (segment of titleSegments(); track $index) {
            @if (segment.match) {
              <mark class="px-0.5">{{ segment.text }}</mark>
            } @else {
              {{ segment.text }}
            }
          }
        </span>
        @if (snippetSegments(); as segments) {
          <span class="mt-1 block text-catalog-caption leading-compact" [class]="active() ? 'text-c2' : 'text-c3'">
            {{ snippetLead() }}
            @for (segment of segments; track $index) {
              @if (segment.match) {
                <mark class="rounded-[var(--r-compact)] bg-acc-soft px-px text-coach-ink ring-1 ring-acc-line">{{
                  segment.text
                }}</mark>
              } @else {
                {{ segment.text }}
              }
            }
            {{ snippetTail() }}
          </span>
        }
        <!-- Длительность со строки ушла, но читалка слышит мету и отметки урока описанием кнопки. -->
        <span class="sr-only" [id]="descId">
          {{ meta() }}
          @if (viewed()) {
            Просмотрено
          }
        </span>
      </span>
    </div>
  `,
})
export class LessonCardComponent {
  /** Заголовок урока сегментами: в списке — один кусок без совпадений, в поиске — с подсветкой. */
  readonly titleSegments = input.required<readonly TextSegment[]>();
  /** Отрывок описания — только в результатах поиска; `null` в обычных списках. */
  readonly snippetSegments = input<readonly TextSegment[] | null>(null);
  /** `…` по краям отрывка, когда окно вырезано из середины описания. */
  readonly snippetLead = input('');
  readonly snippetTail = input('');
  /** «Сейчас играет · 0:24» у играющей, иначе длительность; на строке не видна — звучит для читалки. */
  readonly meta = input('');
  readonly active = input(false);
  /** Ролик этой строки идёт прямо сейчас: на кадре пауза вместо «play». */
  readonly playing = input(false);
  /**
   * Этой строкой урок звучит для читалки. Подсветку (`active`) берут все копии урока в списке,
   * а `aria-current` — только одна из них: иначе читалка повторяла бы «текущий» на каждом дубле.
   */
  readonly current = input(false);
  /** Урок уже смотрели — кадр темнее, в его углу галочка. */
  readonly viewed = input(false);
  /** Постер ролика; `null` — чёрный кадр с одним значком. */
  readonly poster = input<string | null>(null);

  protected readonly descId = `lesson-card-desc-${nextId++}`;
  protected readonly titleText = computed(() =>
    this.titleSegments()
      .map(segment => segment.text)
      .join(''),
  );

  /** Заголовок не влез в две строки — подсказка с полным названием; меряется на входе указателя. */
  protected readonly clamped = signal(false);

  protected measureClamp(title: HTMLElement): void {
    this.clamped.set(title.scrollHeight > title.clientHeight);
  }
}
